import type { Uloga } from '../api/types'

/** Korijen aplikacije za goste; sve ispod njega je površina GOSTA, ne osoblja. */
export const KORIJEN_GOSTA = '/g'

/**
 * Je li adresa u aplikaciji gosta.
 *
 * Nije kozmetika nego granica između dvije sesije. Do zadatka 10 se sesija
 * OSOBLJA podizala pri svakom pokretanju aplikacije, na svakoj adresi — pa je
 * gost koji otvori `/g` osvježavao (i rotirao) sesiju recepcionera zatečenu u
 * istom pregledniku, a cijeli mu je ekran čekao iza tuđeg `/auth/refresh`. Na
 * devu se to vidjelo kao niz uspješnih osvježavanja bez ijednog drugog poziva
 * (zadatak 10 §1); refresh tokeni iz tog niza svi su pripadali recepciji, a ne
 * gostu koji je zurio u vrtuljak.
 */
export function jeGostovaAdresa(putanja: string): boolean {
  return putanja === KORIJEN_GOSTA || putanja.startsWith(`${KORIJEN_GOSTA}/`)
}

/** Javna stranica odgovora na upit (Z22c §A) — vidi <see cref="jeBezSesije"/>. */
export const KORIJEN_UPITA = '/u'

/**
 * Adresa koja NEMA nijednu sesiju (Z22c §A).
 *
 * Odgovor na upit iz e-pošte otvara pružatelj koji Shuttly nema — dokaz mu je
 * token iz adrese, i drugog nema. Podizanje sesije osoblja ondje ne bi ničemu
 * služilo, a rotiralo bi tuđi refresh token onome tko je poruku otvorio na
 * recepcijskom računalu (isti nalaz kao kod gostove adrese, zadatak 10 §1).
 */
/**
 * Javni obrazac postavljanja resorta (O1 §2) — druga adresa istog reda.
 *
 * Ovdje je razlog jači nego kod upita: čovjek koji ovo otvara NEMA nijedan naš
 * račun i ne može ga imati — tek mu ga naša naredba otvara, iz onoga što u ovaj
 * obrazac upiše. Sesija osoblja mu zato ne bi ničemu služila, a rotirala bi tuđi
 * refresh token onome tko je link otvorio na recepcijskom računalu.
 */
export const KORIJEN_POSTAVLJANJA = '/postavljanje'

/**
 * NACRT NASLOVNICE "LIVING STAY" (L1P) — treća adresa bez ijedne sesije.
 *
 * Ovdje razlog nije tuđi refresh token nego to što stranica NEMA što s njim:
 * preview je preslika slike s izmišljenim podacima, bez ijednog poziva na
 * poslužitelj. Podizanje sesije osoblja rotiralo bi tuđu sesiju za ekran koji
 * o resortu ne zna ništa — a otvarat će se upravo ondje gdje je konzola već
 * prijavljena.
 */
export const KORIJEN_PREVIEWA = '/living-preview'

export function jeBezSesije(putanja: string): boolean {
  return [KORIJEN_UPITA, KORIJEN_POSTAVLJANJA, KORIJEN_PREVIEWA].some(
    (korijen) => putanja === korijen || putanja.startsWith(`${korijen}/`),
  )
}

/**
 * Routing po ulozi iz JWT-a (CLAUDE.md §13). Ovo je pogodnost za korisnika, a
 * NE granica ovlasti: granicu drži poslužitelj (politika po ulozi na svakom
 * endpointu). Sakriven izbornik nikoga ne štiti, samo ga ne šalje na 403.
 */
export function pocetnaZaUlogu(uloga: Uloga | null): string {
  switch (uloga) {
    case 'sys_admin':
      // Operater platforme NEMA resort (§4), pa mu "Danas" nema što pokazati:
      // njegova je početna registar, jedini ekran koji mu i pripada.
      return '/sys/tenants'
    case 'vozac':
      // Vozač ostaje u SVOJOJ aplikaciji: fokusirana, mobilna, vezana uz smjenu.
      // Lijevi izbornik sa šifrarnicima bio bi mu smetnja, a ne pomoć.
      return '/vozac'
    case 'porta':
      /*
        PORTIR IMA VLASTITU POČETNU (P3 §1), i to je jedina uloga resorta koja ne
        ide na "Danas".

        "Danas" odgovara na pitanje "ima li išta za mene" i čita `/staff/*` —
        površinu koju portir nema (njegov je svijet dva popisa). Poslati ga onamo
        značilo bi prazan ekran uz tri 403-ice pri svakoj prijavi, a ekran koji
        vodi u zid nije zaštita nego zamka. Njegova početna je rampa, jer je
        rampa jedini razlog zbog kojeg aplikaciju otvara.
      */
      return '/porta'
    case 'tenant_admin':
    case 'direktor':
    case 'osoblje':
    case 'recepcija':
      // JEDNA POČETNA ZA SVE ZAPOSLENIKE RESORTA (NAV-LJUSKA): "Danas" odgovara
      // na pitanje s kojim se aplikacija ujutro otvara — ima li išta za mene.
      // Dotad je svaka uloga ulazila u svoju sekciju i vidjela samo njezin
      // izbornik; sada su svi u istoj ljusci, a razlikuje se što u njoj piše.
      // Direktor i dalje NEMA `/admin` (Z14, odluka 1): READ-ONLY je uloga, ne
      // administrator koji smije manje.
      return '/danas'
    case 'gost':
      // Gost u sesiji OSOBLJA ne bi trebao postojati (prijava gosta ide na
      // `/auth/verify` i u vlastitu sesiju), ali ako se ikad pojavi, put mu je
      // njegova aplikacija — ne konzola.
      return '/g'
    default:
      // Nepoznata ili izostala uloga: nemamo ekran za koji bismo znali da mu
      // pripada, a slanje u tuđi panel bilo bi zid od 403-ica.
      return '/nedostupno'
  }
}
